// CommitteeSubscriptionList.tsx
// Created: 01/27/2025
// Purpose: React component for managing committee subscriptions in "My MISO Calendar"

import React from 'react';
import CommitteeSubscriptionItem, { ICommittee } from './CommitteeSubscriptionItem';

interface ICommitteeSubscriptionListProps { }

interface State {
    committees: Array<ICommittee>;
    loading: boolean;
    error: string | null;
    saving: boolean;
    filter: string;
}

export class CommitteeSubscriptionList extends React.Component<ICommitteeSubscriptionListProps, State> {
    constructor(props: ICommitteeSubscriptionListProps) {
        super(props);
        this.state = {
            committees: [],
            loading: true,
            error: null,
            saving: false,
            filter: ''
        };
        this.toggleSubscription = this.toggleSubscription.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
    }

    public componentDidMount() {
        this.loadCommittees();
    }

    private loadCommittees() {
        this.setState({ loading: true, error: null });

        $.ajax({
            url: '/api/SubscribedCommittees/GetAllCommittees',
            type: 'GET',
            dataType: 'json',
            success: (data) => {
                var committees = data.committees.filter(c => c.entityType !== "Retired" && !c.hideFromAllCommitteeList);
                this.setState({
                    committees: committees || [],
                    loading: false
                });
            },
            error: (xhr) => {
                this.setState({
                    error: xhr.responseJSON?.message || 'Failed to load committees',
                    loading: false
                });
            }
        });
    }

    public toggleSubscription(committee: ICommittee): void {
        const isCurrentlySubscribed = committee.isSubscribed;
        const endpoint = isCurrentlySubscribed
            ? '/api/SubscribedCommittees/Unsubscribe'
            : '/api/SubscribedCommittees/Subscribe';

        // Optimistically update UI
        const updatedCommittees = this.state.committees.map(c => {
            if (c.committeeId === committee.committeeId) {
                return {
                    ...c,
                    isSubscribed: !isCurrentlySubscribed,
                    subscribedDate: !isCurrentlySubscribed ? new Date().toISOString() : null
                };
            }
            return c;
        });

        this.setState({ committees: updatedCommittees, saving: true });

        $.ajax({
            url: endpoint,
            type: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({ committeeId: committee.committeeId }),
            success: () => {
                this.setState({ saving: false });
            },
            error: (xhr) => {
                // Revert on error
                const revertedCommittees = this.state.committees.map(c => {
                    if (c.committeeId === committee.committeeId) {
                        return {
                            ...c,
                            isSubscribed: isCurrentlySubscribed,
                            subscribedDate: committee.subscribedDate
                        };
                    }
                    return c;
                });
                this.setState({
                    committees: revertedCommittees,
                    saving: false,
                    error: xhr.responseJSON?.message || 'Failed to update subscription'
                });

                // Clear error after 5 seconds
                setTimeout(() => this.setState({ error: null }), 5000);
            }
        });
    }

    public handleFilterChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ filter: event.target.value });
    }

    private getFilteredCommittees(): Array<ICommittee> {
        const { committees, filter } = this.state;
        if (!filter.trim()) {
            return committees;
        }

        const lowerFilter = filter.toLowerCase();
        return committees.filter(c =>
            c.name.toLowerCase().includes(lowerFilter) ||
            c.acronym?.toLowerCase().includes(lowerFilter) ||
            c.entityType?.toLowerCase().includes(lowerFilter)
        );
    }

    private getSubscribedCommittees(): Array<ICommittee> {
        return this.getFilteredCommittees().filter(c => c.isSubscribed);
    }

    private getAvailableCommittees(): Array<ICommittee> {
        return this.getFilteredCommittees().filter(c => c.entityType !== 'Workshop' && c.entityType !== 'Workshops' && !c.hideFromAllCommitteeList);
    }

    private getAvailableWorkshops(): Array<ICommittee> {
        return this.getFilteredCommittees().filter(c => !c.hideFromAllCommitteeList && (c.entityType === 'Workshop' || c.entityType == 'Workshops'));
    }

    public render() {
        const { loading, error, saving } = this.state;
        const subscribedCommittees = this.getSubscribedCommittees();
        const availableCommittees = this.getAvailableCommittees();
        const availableWorkshops = this.getAvailableWorkshops();

        if (loading) {
            return (
                <div className="text-center padding-20">
                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                    <p className="margin-top-10">Loading committees...</p>
                </div>
            );
        }

        return (
            <div id="committee-subscriptions" className="committee-subscriptions">
                <div className="row padding-btm-20">
                    <h3 className="col-12 blue">Committee Subscriptions</h3>
                    <p className="col-12 text-muted">
                        Subscribe to committees to automatically add their meetings to your My MISO Calendar.
                    </p>
                </div>

                {error && (
                    <div className="alert alert-danger margin-btm-20">
                        <i className="fa fa-exclamation-circle"></i>&nbsp;{error}
                    </div>
                )}

                {saving && (
                    <div className="alert alert-info margin-btm-20">
                        <i className="fa fa-spinner fa-spin"></i>&nbsp;Saving changes...
                    </div>
                )}

                <div className="row padding-btm-20">
                    <div className="col-12 col-md-6">
                        <div className="input-group">
                            <span className="input-group-text">
                                <i className="fa fa-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search committees..."
                                value={this.state.filter}
                                onChange={this.handleFilterChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="row padding-btm-20">
                    <div className="tab-content-block col-12">
                        <ul className="nav nav-tabs">
                            <li className="nav-item">
                                <a className="nav-link active" data-bs-toggle="tab" data-bs-target="#myCommitteesTab" href="#myCommitteesTab">
                                    My Subscriptions ({subscribedCommittees.length})
                                </a>
                            </li>
                            <li className="nav-item">
                                <a className="nav-link" data-bs-toggle="tab" data-bs-target="#allCommitteesTab" href="#allCommitteesTab">
                                    All Committees ({availableCommittees.length})
                                </a>
                            </li>
                            <li className="nav-item">
                                <a className="nav-link" data-bs-toggle="tab" data-bs-target="#allWorkshopsTab" href="#allWorkshopsTab">
                                    All Workshops ({availableWorkshops.length})
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="tab-content">
                    <div id="myCommitteesTab" className="tab-pane fade show active in">
                        <div className="row">
                            <div className="col-12">
                                {subscribedCommittees.length > 0 ? (
                                    <div className="row">
                                        {subscribedCommittees.map((committee, i) => (
                                            <CommitteeSubscriptionItem
                                                key={committee.committeeId}
                                                committee={committee}
                                                index={i}
                                                onToggle={this.toggleSubscription}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="alert alert-info">
                                        <i className="fa fa-info-circle margin-right-10"></i>
                                        You haven't subscribed to any committees yet. Visit the "All Committees" tab to subscribe.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div id="allCommitteesTab" className="tab-pane fade">
                        <div className="row">
                            <div className="col-12">
                                {availableCommittees.length > 0 ? (
                                    <div className="row">
                                        {availableCommittees.map((committee, i) => (
                                            <CommitteeSubscriptionItem
                                                key={committee.committeeId}
                                                committee={committee}
                                                index={i}
                                                onToggle={this.toggleSubscription}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="alert alert-info">
                                        <i className="fa fa-info-circle margin-right-10"></i>
                                        No committees found matching your search.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div id="allWorkshopsTab" className="tab-pane fade">
                        <div className="row">
                            <div className="col-12">
                                {availableWorkshops.length > 0 ? (
                                    <div className="row">
                                        {availableWorkshops.map((workshop, i) => (
                                            <CommitteeSubscriptionItem
                                                key={workshop.committeeId}
                                                committee={workshop}
                                                index={i}
                                                onToggle={this.toggleSubscription}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="alert alert-info">
                                        <i className="fa fa-info-circle margin-right-10"></i>
                                        No workshops found matching your search.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
